import { useState } from 'react';
import { useField } from 'formik';
import { Eye, EyeOff } from 'lucide-react';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Button } from '@@/buttons';

import { ConnectionField } from './parseConnectionFields';

// Validates a field from the chart's metadata: required fields reject empty
// input (optional stay clearable), and a value must meet any minimum length.
function makeFieldValidator(field: ConnectionField) {
  return (value: unknown) => {
    const trimmed = String(value ?? '').trim();
    if (trimmed === '') {
      return field.required ? 'This field is required.' : undefined;
    }
    if (field.minLength && trimmed.length < field.minLength) {
      return `Must be at least ${field.minLength} characters.`;
    }
    return undefined;
  };
}

/** A single dynamically-generated field row: label above a full-width input,
 * matching the reference wizard layout. Secret-looking fields (name matching
 * key/token/secret/password) render as a masked input with a reveal toggle.
 * When read-only the input is not editable (and required-field validation is
 * skipped, since there is nothing to submit). */
export function ConnectionFieldInput({
  field,
  readOnly,
}: {
  field: ConnectionField;
  readOnly?: boolean;
}) {
  // Field keys are dot-paths (e.g. "image.tag") nested under the form's
  // "values" object, matching the shape Helm expects for values.yaml.
  const name = `values.${field.key}`;
  const [formikField, meta] = useField({
    name,
    // Read-only fields have nothing to submit, so they skip validation.
    validate:
      !readOnly && (field.required || field.minLength)
        ? makeFieldValidator(field)
        : undefined,
  });
  const [visible, setVisible] = useState(false);

  const error = meta.touched ? meta.error : undefined;

  if (!field.isSecret) {
    return (
      <FormControl
        label={field.label}
        size="vertical"
        inputId={name}
        required={field.required}
        errors={error}
      >
        <Input
          id={name}
          name={name}
          value={formikField.value ?? ''}
          onChange={formikField.onChange}
          onBlur={formikField.onBlur}
          placeholder={field.placeholder}
          readOnly={readOnly}
          autoComplete="off"
          data-cy={`addon-install-field-${field.key}`}
        />
      </FormControl>
    );
  }

  return (
    <FormControl
      label={field.label}
      size="vertical"
      inputId={name}
      required={field.required}
      errors={error}
    >
      <div className="relative">
        <Input
          id={name}
          name={name}
          type={visible ? 'text' : 'password'}
          value={formikField.value ?? ''}
          onChange={formikField.onChange}
          onBlur={formikField.onBlur}
          placeholder={field.placeholder}
          readOnly={readOnly}
          className="pr-9"
          autoComplete="off"
          data-cy={`addon-install-field-${field.key}`}
        />
        <Button
          type="button"
          color="link"
          size="small"
          className="!absolute right-2 top-1/2 !m-0 -translate-y-1/2 !p-0"
          onClick={() => setVisible((v) => !v)}
          icon={visible ? EyeOff : Eye}
          title={visible ? 'Hide value' : 'Show value'}
          aria-label={visible ? 'Hide value' : 'Show value'}
          data-cy={`addon-install-field-${field.key}-toggle`}
        />
      </div>
    </FormControl>
  );
}
